import axios from "axios";

import { userPilotAdapter } from "@av/components/utilities/trackings/vendor/user-pilot/UserPilotAdapter";
import { resetSessionStores } from "@av/utils/store/session-store-reset";
import { AUTH_API_URL } from "./common";
import { resetFeatureFlags } from "../features/feature-flags";

export async function login(username: string, password: string): Promise<boolean> {
  const response = await axios.post<boolean>(`${AUTH_API_URL}/login`, {
    username,
    password,
  });

  if (response.status !== 200) {
    throw new Error("Authentication failed");
  }

  if (response.data) {
    resetFeatureFlags();
  }

  userPilotAdapter.clearWebStorage();

  return response.data;
}

export const logout = async () => {
  resetSessionStores();

  try {
    await axios.post(`${AUTH_API_URL}/logout`);
  } finally {
    userPilotAdapter.clearWebStorage();
  }
};

export async function authCheck(ac?: AbortController) {
  const result = await axios.get(
    `${AUTH_API_URL}/auth-check`,
    ac && {
      signal: ac.signal,
    }
  );

  if (result.status !== 200) {
    throw Error("Auth failed");
  }
}

export type MagicLinkResponse = {
  expiresIn?: number;
};

export async function generateMagicLink(
  username: string,
  locale = "en-CA",
  returnTo?: string
): Promise<MagicLinkResponse> {
  const result = await axios.post<MagicLinkResponse>(`${AUTH_API_URL}/magic-link`, {
    username,
    locale,
    returnTo,
  });

  if (result.status !== 200) {
    throw Error("Magic link generation failed");
  }

  return result.data;
}

export async function loginWithMagicLink(token: string, ac?: AbortController): Promise<boolean> {
  const response = await axios.post<boolean>(
    `${AUTH_API_URL}/magic-link-login`,
    {
      token,
    },
    ac && {
      signal: ac.signal,
    }
  );

  if (response.status !== 200) {
    throw new Error("Magic link authentication failed");
  }

  // Password login logs out first, but a magic link can sign in over an expired session in the
  // same tab, so the previous user's session stores are reset here once the login succeeds.
  if (response.data) {
    resetSessionStores();
    resetFeatureFlags();
  }

  userPilotAdapter.clearWebStorage();

  return response.data;
}
